<template>
    <div>
        <v-card-title v-if="title" class="mx-0 px-0">
            {{ title }}
        </v-card-title>
        <v-card-text v-if="showMetadata && form" class="ma-0 pa-0">
            <div class="mb-3" v-if="form.expire_date">
                <v-icon class="mr-2">{{ icons.mdiCalendarToday }}</v-icon>
                {{ $t("Vastausaika päättyy") }} {{ getDateTime(form.expire_date) }}
            </div>
            <div class="mb-3" v-if="form.anonymous">
                <v-icon class="mr-2">{{ icons.mdiEyeOffOutline }}</v-icon>
                {{ $t("Vastaukset kerätään anonyymisti") }}
            </div>
            <div class="mb-3" v-else>
                <v-icon class="mr-2">{{ icons.mdiEyeOutline }}</v-icon>
                {{ $t("Vastauksiin liitetään nimi automaattisesti") }}
            </div>
            <div class="mb-3" v-if="form.allow_multiple_submissions">
                <v-icon class="mr-2">{{ icons.mdiRestart }}</v-icon>
                {{ $t("Voit vastata kyselyyn useita kertoja") }}
            </div>
            <br>
        </v-card-text>
        <v-card-text v-if="description" class="ma-0 pa-0">
            <div v-html="description"></div>
            <v-divider class="my-5"></v-divider>
        </v-card-text>
        <v-card-text v-if="showSuccessMessage" class="ma-0 pa-0">
            <v-alert text type="success">
                {{ $t('Olet vastannut kyselyyn') }}
            </v-alert>
        </v-card-text>
        <v-card-text v-if="!showSuccessMessage" class="ma-0 pa-0">
            <v-form ref="formRef">
                <v-card outlined v-for="field in form.fields" :key="field.id" class="mb-2">

                    <v-card-text>
                        <label class="form-renderer__label text-body-2 font-weight-medium d-block mb-2">
                            {{ field.label }}<span v-if="field.required" class="error--text ml-1">*</span>
                        </label>
                        <template v-if="field.field_type === 1 && field.datatype === 'date'">
                            <v-menu v-model="dateMenus[field.id]" :close-on-content-click="false"
                                transition="scale-transition" offset-y min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field v-model="internalResponses[field.id]" outlined
                                        :placeholder="$t('Valitse päivämäärä')" :hint="field.help_text"
                                        :persistent-hint="!!field.help_text" :disabled="disabled"
                                        :rules="field.required ? [v => !!v || $t('Tämä tieto on pakollinen')] : []"
                                        readonly v-bind="attrs" v-on="on">
                                        <template v-slot:prepend-inner>
                                            <v-icon>{{ icons.mdiCalendar }}</v-icon>
                                        </template>
                                    </v-text-field>
                                </template>
                                <v-date-picker v-model="internalResponses[field.id]" no-title scrollable
                                    :locale="locale" :first-day-of-week="1" :disabled="disabled"
                                    @input="dateMenus[field.id] = false"></v-date-picker>
                            </v-menu>
                        </template>

                        <v-text-field v-else-if="field.field_type === 1" v-model="internalResponses[field.id]" outlined
                            :placeholder="$t('Kirjoita vastaus')" :required="field.required" :hint="field.help_text"
                            :persistent-hint="!!field.help_text" :disabled="disabled"
                            :rules="field.required ? [v => !!v || $t('Tämä tieto on pakollinen')] : []"></v-text-field>

                        <v-textarea v-else-if="field.field_type === 2" v-model="internalResponses[field.id]" outlined
                            :placeholder="$t('Kirjoita vastaus')" :required="field.required" :hint="field.help_text"
                            :persistent-hint="!!field.help_text" :disabled="disabled"
                            :rules="field.required ? [v => !!v || $t('Tämä tieto on pakollinen')] : []"></v-textarea>

                        <v-radio-group v-else-if="field.field_type === 3" v-model="internalResponses[field.id]"
                            class="ma-0 mb-2" :required="field.required" :hint="field.help_text"
                            :persistent-hint="!!field.help_text" :disabled="disabled"
                            :rules="field.required ? [v => !!v || $t('Tämä tieto on pakollinen')] : []">
                            <v-radio v-for="option in field.options" :key="option.id" :label="option.label"
                                :value="option.id"></v-radio>
                        </v-radio-group>

                        <div v-else-if="field.field_type === 4">
                            <div class="mb-5">
                                <div v-if="field.help_text" class="text-caption mt-1">{{ field.help_text }}</div>

                                <v-checkbox v-for="option in field.options" :key="option.id" class="mt-1"
                                    v-model="internalResponses[field.id]" :label="option.label" :value="option.id"
                                    :disabled="disabled" hide-details></v-checkbox>

                                <div class="pb-5">
                                    <v-input v-if="field.required" v-model="internalResponses[field.id]"
                                        :rules="[v => (v && v.length > 0) || $t('Valitse vähintään yksi vaihtoehto')]"
                                        style="height: 0; margin: 0; padding: 0;">
                                    </v-input>
                                </div>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-form>
        </v-card-text>
    </div>
</template>

<script>
import { ref, watch, computed } from "@vue/composition-api";
import { mdiEyeOffOutline, mdiEyeOutline, mdiCalendarToday, mdiRestart, mdiCalendar } from "@mdi/js";
import { getDateTime } from "@/@core/utils/datetime_helpers";
import { i18n } from "@/plugins/i18n";

export default {
    name: "FormRenderer",
    props: {
        form: {
            type: Object,
            required: true
        },
        value: {
            type: Object,
            default: () => ({})
        },
        showMetadata: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        title: {
            type: String,
            default: null
        },
        description: {
            type: String,
            default: null
        },
        showSuccessMessage: {
            type: Boolean,
            default: false
        }
    },
    setup(props, { emit }) {
        const formRef = ref(null);
        const internalResponses = ref({});
        const dateMenus = ref({});
        const locale = i18n.locale;

        // Initialize responses when form changes
        watch(() => props.form, (newForm) => {
            if (newForm && newForm.fields) {
                const initialResponses = {};
                newForm.fields.forEach(field => {
                    if (field.field_type === 4) {
                        initialResponses[field.id] = [];  // Checkbox - array
                    }
                    else if (field.field_type === 3) {
                        initialResponses[field.id] = null;  // Checkbox - array
                    } else {
                        initialResponses[field.id] = '';   // Other fields - empty string
                    }
                });
                internalResponses.value = initialResponses;
                emit('input', initialResponses);
            }
        }, { immediate: true });

        // Emit changes to parent
        watch(internalResponses, (newResponses) => {
            emit('input', newResponses);
        }, { deep: true });

        // Watch for external changes to value
        watch(() => props.value, (newValue) => {
            if (newValue && JSON.stringify(newValue) !== JSON.stringify(internalResponses.value)) {
                internalResponses.value = { ...newValue };
            }
        }, { deep: true });

        const getFormattedResponses = () => {
            return Object.entries(internalResponses.value).map(([fieldId, value]) => {
                const field = props.form.fields.find(f => f.id == fieldId);

                if (field.field_type === 3 || field.field_type === 4) {
                    // Radio or checkbox
                    return {
                        field_id: parseInt(fieldId),
                        selected_option_ids: Array.isArray(value) ? value :
                            !!value ? [value] : []
                    };
                } else {
                    // Text or textarea
                    return {
                        field_id: parseInt(fieldId),
                        text_value: value
                    };
                }
            });
        };

        // Validation method exposed to parent
        const validate = () => {
            return formRef.value ? formRef.value.validate() : false;
        };

        return {
            formRef,
            internalResponses,
            dateMenus,
            validate,
            getFormattedResponses,
            getDateTime,
            locale,
            icons: {
                mdiCalendarToday,
                mdiEyeOffOutline,
                mdiEyeOutline,
                mdiRestart,
                mdiCalendar
            }
        };
    }
};
</script>
